import { useMemo } from "react";
import { Calendar, Star, Sun } from "lucide-react";

/**
 * Hebrew month start dates (approximate Gregorian mapping).
 * Same logic used in backend/_detect_hebrew_month.
 */
const HEBREW_MONTHS = [
  { name: "Nisan",    gMonth: 3,  gDay: 22 },
  { name: "Iyar",     gMonth: 4,  gDay: 21 },
  { name: "Sivan",    gMonth: 5,  gDay: 21 },
  { name: "Tammuz",   gMonth: 6,  gDay: 20 },
  { name: "Av",       gMonth: 7,  gDay: 20 },
  { name: "Elul",     gMonth: 8,  gDay: 19 },
  { name: "Tishrei",  gMonth: 9,  gDay: 17 },
  { name: "Cheshvan", gMonth: 10, gDay: 17 },
  { name: "Kislev",   gMonth: 11, gDay: 16 },
  { name: "Tevet",    gMonth: 12, gDay: 16 },
  { name: "Shevat",   gMonth: 1,  gDay: 15 },
  { name: "Adar",     gMonth: 2,  gDay: 14 },
];

const MONTH_DATA = {
  Nisan:    { theme: "Redemption & Miracles",        scripture: "The LORD brought us out with a mighty hand. — Deut 26:8",          feast: "Passover (Pesach)", feastNote: "Nisan 15" },
  Iyar:     { theme: "Healing & Counting",           scripture: "I am the LORD who heals you. — Exodus 15:26",                     feast: null, feastNote: null },
  Sivan:    { theme: "Revelation & Torah",            scripture: "God spoke all these words. — Exodus 20:1",                        feast: "Pentecost (Shavuot)", feastNote: "Sivan 6" },
  Tammuz:   { theme: "Vision & Sight",               scripture: "The eyes of the LORD range throughout the earth. — 2 Chr 16:9",   feast: null, feastNote: null },
  Av:       { theme: "Comfort & Restoration",         scripture: "Comfort, comfort my people, says your God. — Isaiah 40:1",       feast: null, feastNote: null },
  Elul:     { theme: "Repentance & Return",           scripture: "Return to me, and I will return to you. — Malachi 3:7",          feast: null, feastNote: null },
  Tishrei:  { theme: "New Beginnings & Awe",          scripture: "Behold, I make all things new. — Revelation 21:5",               feast: "Rosh Hashanah / Yom Kippur / Sukkot", feastNote: "Tishrei 1, 10, 15" },
  Cheshvan: { theme: "Faithful Planting",             scripture: "Those who sow in tears will reap with shouts of joy. — Ps 126:5", feast: null, feastNote: null },
  Kislev:   { theme: "Light & Dedication",            scripture: "The people walking in darkness have seen a great light. — Isa 9:2", feast: "Hanukkah", feastNote: "Kislev 25" },
  Tevet:    { theme: "Holy Zeal",                     scripture: "Zeal for your house consumes me. — Psalm 69:9",                  feast: null, feastNote: null },
  Shevat:   { theme: "Trees & New Life",              scripture: "He shall be like a tree planted by rivers of water. — Psalm 1:3", feast: "Tu B'Shevat", feastNote: "Shevat 15" },
  Adar:     { theme: "Joy & Victory",                 scripture: "The Jews had light, gladness, joy, and honor. — Esther 8:16",    feast: "Purim", feastNote: "Adar 14" },
};

function detectHebrewMonth(now) {
  const year = now.getFullYear();
  const currentDoy = Math.floor((now - new Date(year, 0, 1)) / 86400000) + 1;

  const starts = HEBREW_MONTHS.map(m => {
    const d = new Date(year, m.gMonth - 1, m.gDay);
    const doy = Math.floor((d - new Date(year, 0, 1)) / 86400000) + 1;
    return { name: m.name, doy };
  }).sort((a, b) => a.doy - b.doy);

  let detected = starts[starts.length - 1].name;
  for (const s of starts) {
    if (currentDoy >= s.doy) detected = s.name;
  }
  return detected;
}

function hebrewYear(now) {
  const gy = now.getFullYear();
  const gm = now.getMonth(); // 0-indexed
  // Before Tishrei (~Sep), Hebrew year = GY + 3760; after Tishrei, GY + 3761
  return gm >= 8 ? gy + 3761 : gy + 3760;
}

export default function HebrewCalendarWidget() {
  const data = useMemo(() => {
    const now = new Date();
    const month = detectHebrewMonth(now);
    const info = MONTH_DATA[month] || {};
    return {
      gregorian: now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
      hebrewMonth: month,
      hebrewYear: hebrewYear(now),
      theme: info.theme || "",
      scripture: info.scripture || "",
      feast: info.feast || null,
      feastNote: info.feastNote || null,
    };
  }, []);

  return (
    <div className="bg-gradient-to-br from-navy via-navy to-[#0A111F] border border-gold/30 rounded-2xl p-6 relative overflow-hidden shadow-xl shadow-black/30 group" data-testid="hebrew-calendar-widget">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none transition-opacity duration-700 group-hover:opacity-10">
        <Star size={120} className="text-gold" />
      </div>
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-gold/5 rounded-full blur-3xl"></div>

      {/* Header */}
      <div className="flex items-center gap-4 mb-5 relative z-10">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/20 flex items-center justify-center shrink-0 shadow-inner">
          <Calendar className="h-6 w-6 text-gold" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-bold text-white tracking-wide">Hebrew Calendar</h3>
          <p className="text-xs font-medium text-white/60 tracking-wider uppercase mt-0.5">{data.gregorian}</p>
        </div>
      </div>

      {/* Hebrew month + year */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-4 mb-4 relative z-10 hover:bg-white/10 transition-colors duration-300">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-heading font-bold text-gold drop-shadow-sm">{data.hebrewMonth}</span>
          <span className="text-sm font-semibold text-white/80 tracking-widest">{data.hebrewYear}</span>
        </div>
        <p className="text-sm text-white/90 mt-1.5 flex items-center gap-2 font-medium">
          <Sun size={14} className="text-gold shrink-0 animate-spin-slow" />
          Month of {data.theme}
        </p>
      </div>

      {/* Scripture */}
      <div className="px-2 relative z-10 border-l-2 border-gold/40 pl-4 py-1 mb-4">
        <p className="text-sm text-white/80 italic leading-relaxed font-heading" data-testid="hebrew-scripture">
          "{data.scripture}"
        </p>
      </div>

      {/* Feast day */}
      {data.feast && (
        <div className="mt-2 flex items-center gap-3 bg-gradient-to-r from-gold/10 to-transparent border border-gold/20 rounded-xl px-4 py-3 relative z-10" data-testid="hebrew-feast">
          <Star size={16} className="text-gold shrink-0 fill-gold/50" />
          <div>
            <p className="text-sm text-gold font-bold tracking-wide">{data.feast}</p>
            <p className="text-xs text-white/60 font-medium uppercase tracking-widest mt-0.5">{data.feastNote}</p>
          </div>
        </div>
      )}
    </div>
  );
}
